import { createContext, useContext, useMemo } from 'react';

import type { StoredCaptureEvent } from '../../lib/types';

export interface SpanEvent extends Omit<StoredCaptureEvent, 'phase'> {
  phase: 'span';
  requestPayload?: unknown;
  responsePayload?: unknown;
  errorPayload?: unknown;
  fnName?: string | undefined;
}

export type ProcessedEvent = (StoredCaptureEvent | SpanEvent) & {
  fnName?: string | undefined;
  url?: string | undefined;
  displayWarning?: boolean | undefined;
};

export interface ExecutionData {
  executionId: string;
  projectId: string;
  agentUrl: string | null;
  rawEvents: StoredCaptureEvent[];
  events: ProcessedEvent[];
  spans: SpanEvent[];
  consoleLogs: ProcessedEvent[];
  networkCalls: ProcessedEvent[];
  databaseQueries: ProcessedEvent[];
  durationMs: number;
  startAt: number;
  endAt: number;
  status: 'success' | 'error' | 'running';
  bootEvent?: StoredCaptureEvent | undefined;
  returnEvent?: StoredCaptureEvent | undefined;
  errorEvent?: ProcessedEvent | undefined;
}

export interface ProcessedExecutionData {
  events: ProcessedEvent[];
  spans: SpanEvent[];
  consoleLogs: ProcessedEvent[];
  networkCalls: ProcessedEvent[];
  databaseQueries: ProcessedEvent[];
  durationMs: number;
  startAt: number;
  endAt: number;
  status: 'success' | 'error' | 'running';
  bootEvent?: StoredCaptureEvent | undefined;
  returnEvent?: StoredCaptureEvent | undefined;
  errorEvent?: ProcessedEvent | undefined;
}

const InspectorContext = createContext<ExecutionData | null>(null);

function asString(value: unknown): string | null {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return null;
}

function cleanUrl(input: string, max = 40): string {
  try {
    const u = new URL(input);
    const p = u.pathname;
    if (p.length > max) {
      return p.substring(0, max / 2) + '…' + p.substring(p.length - max / 2);
    }
    return p === '/' ? u.hostname : p;
  } catch {
    return input.length > max ? input.substring(0, max) + '…' : input;
  }
}

function extractFnName(
  capability: string,
  action: string,
  requestPayload?: unknown,
  responsePayload?: unknown,
): string | undefined {
  if (capability === 'vm') {
    const p = (requestPayload ?? responsePayload ?? {}) as Record<string, unknown>;
    return asString(p?.['name']) ?? `vm.${action}`;
  }
  if (capability === 'rpc' && action === 'call') {
    const rp = (requestPayload ?? responsePayload ?? {}) as Record<string, unknown>;
    const moduleName = asString(rp?.['module']);
    const methodName = asString(rp?.['method']);
    if (moduleName != null && methodName != null) return `${moduleName}.${methodName}`;
    let rpcName = moduleName ?? asString(rp['0']) ?? asString(rp?.['name']) ?? 'rpc';
    if (rpcName.includes('_')) {
      const parts = rpcName.split('_');
      rpcName = parts.at(-1) ?? rpcName;
      if (!rpcName) rpcName = 'rpc';
    }
    return rpcName;
  }

  if (capability === 'fetch') {
    const p = (requestPayload ?? responsePayload ?? {}) as Record<string, unknown>;
    const init = (p['init'] as Record<string, unknown> | undefined) ?? {};
    const urlStr = Boolean(p['0'])
      ? (asString(p['0']) ?? '')
      : (asString(p['input']) ?? asString(p['url']) ?? '');
    const method = (asString(init?.['method']) ?? asString(p['method']) ?? 'GET').toUpperCase();
    if (urlStr) {
      return `${method} ${cleanUrl(urlStr)}`;
    }
    return 'fetch';
  }

  return undefined;
}

export function InspectorDataProvider({
  executionId,
  projectId,
  agentUrl,
  rawEvents,
  children,
}: {
  executionId: string;
  projectId: string;
  agentUrl: string | null;
  rawEvents: StoredCaptureEvent[];
  children: React.ReactNode;
}): import('react/jsx-runtime').JSX.Element {
  const data = useMemo<ExecutionData>(() => {
    const processed = processExecutionEvents(rawEvents);
    return {
      executionId,
      projectId,
      agentUrl,
      rawEvents,
      ...processed,
    };
  }, [executionId, projectId, agentUrl, rawEvents]);

  return <InspectorContext.Provider value={data}>{children}</InspectorContext.Provider>;
}

export function processExecutionEvents(rawEvents: StoredCaptureEvent[]): ProcessedExecutionData {
  const events: ProcessedEvent[] = [];
  const activeSpans = new Map<string, SpanEvent>();
  const spans: SpanEvent[] = [];
  const consoleLogs: ProcessedEvent[] = [];
  const networkCalls: ProcessedEvent[] = [];
  const databaseQueries: ProcessedEvent[] = [];

  let status: 'success' | 'error' | 'running' = 'running';
  let bootEvent: StoredCaptureEvent | undefined;
  let returnEvent: StoredCaptureEvent | undefined;
  let errorEvent: ProcessedEvent | undefined;

  // First pass to fold requests/responses
  for (const e of rawEvents) {
    if (e.capability === 'vm' && e.action === 'boot') bootEvent = e;
    if (e.capability === 'vm' && e.action === 'return') {
      returnEvent = e;
      status = 'success';
    }
    if (e.phase === 'error') {
      status = 'error';
    }

    if (e.phase === 'request') {
      const span: SpanEvent = {
        ...e,
        phase: 'span',
        requestPayload: e.payload,
        fnName: extractFnName(e.capability, e.action, e.payload),
      };
      activeSpans.set(e.id, span);
      events.push(span);
      spans.push(span);

      switch (e.capability) {
        case 'console':
          consoleLogs.push(span);
          break;
        case 'fetch':
          networkCalls.push(span);
          break;
        case 'db':
          databaseQueries.push(span);
          break;
      }
    } else if (e.phase === 'response' || e.phase === 'error') {
      const span = activeSpans.get(e.id);
      if (span) {
        if (e.phase === 'response') span.responsePayload = e.payload;
        if (e.phase === 'error') {
          span.errorPayload = e.payload;
          errorEvent = span;
        }
        span.durationMs = e.durationMs ?? span.durationMs ?? 0;
        const _nfn = extractFnName(
          span.capability,
          span.action,
          span.requestPayload ?? span.responsePayload,
        );
        if (span.fnName == null && _nfn != null) span.fnName = _nfn;
      } else {
        const fnName = extractFnName(e.capability, e.action, e.payload);
        const ev = { ...e, fnName };
        events.push(ev);
        if (e.phase === 'error') errorEvent = ev;
        switch (e.capability) {
          case 'console':
            consoleLogs.push(ev);
            break;
          case 'fetch':
            networkCalls.push(ev);
            break;
          case 'db':
            databaseQueries.push(ev);
            break;
        }
      }
    } else {
      const ev = { ...e, fnName: extractFnName(e.capability, e.action, e.payload) };
      events.push(ev);
      switch (e.capability) {
        case 'console':
          consoleLogs.push(ev);
          break;
        case 'fetch':
          networkCalls.push(ev);
          break;
        case 'db':
          databaseQueries.push(ev);
          break;
      }
    }
  }

  const startAt = events[0]?.at ?? 0;
  let maxAt = 0;
  for (const e of events) {
    const eEnd = e.at + (e.durationMs ?? 0);
    if (eEnd > maxAt) maxAt = eEnd;
  }

  return {
    events,
    spans,
    consoleLogs,
    networkCalls,
    databaseQueries,
    durationMs: Math.max(0, maxAt - startAt),
    startAt,
    endAt: maxAt,
    status,
    bootEvent,
    returnEvent,
    errorEvent,
  };
}

export function useExecutionData(): ExecutionData {
  const ctx = useContext(InspectorContext);
  if (!ctx) throw new Error('useExecutionData must be used within InspectorDataProvider');
  return ctx;
}
